/**
 * Tiny CSV utilities. RFC 4180 compliant.
 *
 * Why hand-rolled instead of a library:
 *   - This is ~50 lines. PapaParse is ~80KB gzipped.
 *   - Our needs are export-only (no parsing), so we don't need the full
 *     surface area.
 */

/**
 * Escape a single field for CSV. Wraps in double quotes if the value
 * contains a comma, newline, carriage return, or double quote. Internal
 * double quotes are doubled.
 */
function escapeField(raw: unknown): string {
  if (raw == null) return ""
  const s = typeof raw === "string" ? raw : String(raw)
  // Need quoting if it contains separators or quotes.
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/**
 * Serialize an array of rows into a CSV string. Row keys must match the
 * provided headers (column order is taken from headers).
 */
export function toCSV<T extends Record<string, unknown>>(
  headers: { key: keyof T & string; label: string }[],
  rows: T[]
): string {
  const headerLine = headers.map((h) => escapeField(h.label)).join(",")
  const bodyLines = rows.map((row) =>
    headers.map((h) => escapeField(row[h.key])).join(",")
  )
  // Use CRLF per spec — Excel and Google Sheets are happy with both, but
  // CRLF is technically the spec.
  return [headerLine, ...bodyLines].join("\r\n")
}

/**
 * Trigger a browser download for the given CSV string. Uses a Blob URL +
 * anchor click (works in every modern browser; no extra deps).
 */
export function downloadCSV(filename: string, csv: string): void {
  // Prepend BOM so Excel detects UTF-8 correctly when opened directly.
  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Today's date as YYYY-MM-DD (local time). Useful for export filenames.
 */
export function todayIso(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}
